import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Copy, Check, Eye, RefreshCw } from 'lucide-react';
import { instances } from '../api';

/**
 * Der Medusa-API-Schlüssel für den ojfm-Konnektor — erzeugen und abrufen im
 * PC, ohne Container-Terminal (Nico Mueck, 03.11.2026).
 *
 * Der Wert wird nur auf Klick geladen und nicht im Query-Cache gehalten: er
 * soll nicht in einer offenen Seite herumliegen, die jemand anderes später
 * ansieht.
 */
export default function MedusaSchluesselKarte({ instanceId, darf }: { instanceId: string; darf: boolean }) {
  const qc = useQueryClient();
  const [wert, setWert] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [hinweis, setHinweis] = useState('');
  const [fehler, setFehler] = useState('');

  const { data: stand } = useQuery({
    queryKey: ['instances', instanceId, 'medusa-schluessel'],
    queryFn: () => instances.medusaSchluessel(instanceId),
  });

  const alsFehler = (err: unknown) =>
    setFehler((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Aktion fehlgeschlagen.');

  const erzeugen = useMutation({
    mutationFn: () => instances.medusaSchluesselErzeugen(instanceId),
    onSuccess: (r) => {
      setFehler('');
      setWert(r.schluessel);
      setHinweis(r.widerrufen
        ? 'Neuer Schlüssel erzeugt, der bisherige ist widerrufen. In ojfm den neuen eintragen.'
        : 'Schlüssel erzeugt. Jetzt in ojfm eintragen.');
      qc.invalidateQueries({ queryKey: ['instances', instanceId, 'medusa-schluessel'] });
    },
    onError: alsFehler,
  });

  const anzeigen = useMutation({
    mutationFn: () => instances.medusaSchluesselWert(instanceId),
    onSuccess: (r) => { setFehler(''); setWert(r.schluessel); },
    onError: alsFehler,
  });

  const kopieren = async () => {
    if (!wert) return;
    try {
      await navigator.clipboard.writeText(wert);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      setFehler('Kopieren nicht möglich — bitte von Hand markieren.');
    }
  };

  if (!stand) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-700">API-Schlüssel für ojfm</h3>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Damit schickt ojfm Titel, Preise und Kategorien an diesen Shop. In ojfm unter
        Verwaltung → Shop-Integrationen → „Medusa“ eintragen, mit der Shop-URL{' '}
        <code className="font-mono">{stand.shopUrl ?? '—'}</code>. Vorher dort die
        Mandanten-Freischaltung <code className="font-mono">shop_medusa_v1</code> einschalten.
      </p>

      <div className="mt-3 text-sm text-slate-700">
        {stand.vorhanden
          ? <>Hinterlegt: <code className="font-mono">{stand.praefix}…</code></>
          : <span className="text-slate-500">Noch kein Schlüssel im PC.</span>}
      </div>

      {wert && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <code className="min-w-0 flex-1 break-all font-mono text-xs text-slate-800">{wert}</code>
          <button
            type="button"
            onClick={kopieren}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-white"
          >
            {kopiert ? <><Check size={12} /> Kopiert</> : <><Copy size={12} /> Kopieren</>}
          </button>
        </div>
      )}

      {darf && (
        <div className="mt-3 flex flex-wrap gap-2">
          {stand.vorhanden && !wert && (
            <button
              type="button"
              onClick={() => anzeigen.mutate()}
              disabled={anzeigen.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <Eye size={12} /> Anzeigen
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!stand.vorhanden || confirm('Neuen Schlüssel erzeugen? Der bisherige wird in Medusa widerrufen — ojfm braucht danach den neuen.'))
                erzeugen.mutate();
            }}
            disabled={erzeugen.isPending}
            className={stand.vorhanden
              ? 'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50'
              : 'inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs text-white hover:bg-brand-700 disabled:opacity-50'}
          >
            <RefreshCw size={12} className={erzeugen.isPending ? 'animate-spin' : ''} />
            {erzeugen.isPending ? 'Erzeuge…' : stand.vorhanden ? 'Neu erzeugen' : 'Schlüssel erzeugen'}
          </button>
        </div>
      )}

      <div className="mt-4 border-t border-slate-100 pt-3">
        <h4 className="text-xs font-semibold text-slate-600">Cover und Bilder (Verteilungsprofil „Medusa-Shop“ in ojfm)</h4>
        {stand.medien ? (
          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-400">Host</dt><dd className="font-mono text-slate-700">{stand.medien.host}</dd>
            <dt className="text-slate-400">Bucket</dt><dd className="font-mono text-slate-700">{stand.medien.bucket}</dd>
            <dt className="text-slate-400">secret_ref</dt><dd className="font-mono text-slate-700">{stand.medien.secretRef}</dd>
            <dt className="text-slate-400">Medien-Basis-URL</dt><dd className="font-mono text-slate-700">{stand.medien.basisUrl}</dd>
          </dl>
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            Ohne Modul <code className="font-mono">s3</code> hat der Shop keinen Medienspeicher — Titel und Preise
            kommen an, Cover nicht.
          </p>
        )}
        {stand.medien && (
          <p className="mt-2 text-xs text-slate-500">
            Den Zugang (nur Hochladen) bekommt ojfm über die Verknüpfung, sobald ojfm neu ausgerollt ist —
            hier steht nur der Verweis.
          </p>
        )}
      </div>

      {hinweis && <p className="mt-2 text-xs text-emerald-700">{hinweis}</p>}
      {fehler && <p className="mt-2 text-xs text-red-700">{fehler}</p>}
    </div>
  );
}
